
import { useState, useEffect } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { useChurchBranding } from "@/contexts/ChurchBrandingContext";
import { usePremiumUpgrade } from "./hooks/usePremiumUpgrade";
import { ContactFormView, ChurchFormValues } from "./church-form/ContactFormView";
import { SuccessView } from "./church-form/SuccessView";
import { SubscriptionSheetView } from "./church-form/SubscriptionSheetView";

interface ChurchContactFormProps {
  onClose: () => void;
}

export function ChurchContactForm({ onClose }: ChurchContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const session = useSession();
  const { branding } = useChurchBranding();
  const { 
    handleChurchUpgrade, 
    isLoading: isSubscribing, 
    error: subscriptionError,
    clearError
  } = usePremiumUpgrade();
  const [showSubscribeSheet, setShowSubscribeSheet] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const primaryColor = branding?.primaryColor || "#1EAEDB";

  // Force scroll to top on component mount
  useEffect(() => {
    // Immediate scroll
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Find the containing dialog/modal and scroll it too
    const parentDialog = document.querySelector('[role="dialog"], .max-h-\\[90vh\\]');
    if (parentDialog instanceof HTMLElement) {
      parentDialog.scrollTop = 0;
    }
  }, [isSuccess, showSubscribeSheet]); // Re-run on state changes that affect layout

  // Update error message when subscription error changes
  useEffect(() => {
    if (subscriptionError) {
      setErrorMessage(subscriptionError.message);
    }
  }, [subscriptionError]);

  const onSubmit = async (data: ChurchFormValues) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    
    try {
      const payload = {
        title: "Church Plan Inquiry",
        name: data.contactName,
        phone: data.contactPhone,
        organization: `${data.churchName} - ${data.churchCity}`,
        email: data.contactEmail,
        message: data.additionalInfo || "Interested in Church Plan",
        source: "Grace Awakens Premium Page"
      };

      console.log("Sending data to webhook:", payload);
      
      const response = await fetch("https://hook.us2.make.com/9g4b5j7jaubnpe9ugqeh5ej2wduogoxy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error("Webhook error response:", await response.text());
        throw new Error("Failed to submit form");
      }
      
      // Success handling - force scroll to top
      setIsSuccess(true);
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } catch (error) {
      console.error("Error submitting church contact form:", error);
      setErrorMessage("There was a problem submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribeNow = () => {
    setErrorMessage(null);
    clearError();
    setShowSubscribeSheet(true);
    
    // Force scroll to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const proceedWithSubscription = async () => {
    setErrorMessage(null);
    try {
      // This will get the checkout URL but not automatically redirect
      const checkoutUrl = await handleChurchUpgrade(false);
      
      if (checkoutUrl) {
        // Manual redirect if URL is returned
        window.location.href = checkoutUrl;
      }
    } catch (error) {
      console.error("Error processing church subscription:", error);
    }
  };

  return (
    <div className="max-h-[90vh] overflow-y-auto">
      <div className="p-6 bg-white rounded-lg">
        <div className="flex flex-col items-center mb-6">
          <img 
            src="/lovable-uploads/63dc6fcc-849b-4d08-a3d0-a0fa7dfa124d.png" 
            alt="Grace Awakens Logo" 
            className="h-16 mb-4"
          />
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-2" style={{ color: primaryColor }}>Church Plan Inquiry</h2>
            <div className="w-16 h-1 mx-auto rounded" style={{ backgroundColor: primaryColor }}></div>
          </div>
        </div>
        
        {isSuccess ? (
          <SuccessView 
            onClose={onClose}
            onSubscribeNow={handleSubscribeNow}
            isSubscribing={isSubscribing}
            errorMessage={errorMessage}
            primaryColor={primaryColor}
          />
        ) : (
          <ContactFormView 
            onSubmit={onSubmit}
            onClose={onClose}
            isSubmitting={isSubmitting}
            errorMessage={errorMessage}
            defaultEmail={session?.user?.email || ""}
            primaryColor={primaryColor}
          />
        )}
      </div>

      <SubscriptionSheetView 
        open={showSubscribeSheet}
        onOpenChange={setShowSubscribeSheet}
        onSubscribe={proceedWithSubscription}
        isSubscribing={isSubscribing}
        errorMessage={errorMessage}
        primaryColor={primaryColor}
      />
    </div>
  );
}
